import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PAGE_SIZE = 25

// `view` is which jobs are listed: the active ones (everything not ignored), the bookmarked ones,
// or the ignored ones.
const defaultFilters = () => ({ q: '', job_title_id: '', job_location_id: '', status: 'all', posted: '', sort: 'newest', view: 'active' })

// Whether a job with these marks is listed in a view.
const inView = (view, job) => {
  if (view === 'bookmarked') return Boolean(job.bookmarked_at)
  if (view === 'ignored') return Boolean(job.ignored_at)
  return !job.ignored_at
}

// A job's marks after bookmarking or ignoring it. The two exclude each other, as on the server.
function marksAfter(job, mark, value) {
  const now = new Date().toISOString()
  if (mark === 'bookmarked') {
    return { bookmarked_at: value ? job.bookmarked_at || now : null, ignored_at: value ? null : job.ignored_at || null }
  }
  return { ignored_at: value ? job.ignored_at || now : null, bookmarked_at: value ? null : job.bookmarked_at || null }
}

function buildQuery(filters, page) {
  const params = new URLSearchParams()
  params.set('page[number]', page)
  params.set('page[limit]', PAGE_SIZE)
  if (filters.q.trim()) params.set('q', filters.q.trim())
  if (filters.job_title_id) params.set('job_title_id', filters.job_title_id)
  if (filters.job_location_id) params.set('job_location_id', filters.job_location_id)
  if (filters.status !== 'all') params.set('status', filters.status)
  if (filters.posted) params.set('posted', filters.posted)
  if (filters.view !== 'active') params.set('view', filters.view)
  params.set('sort', filters.sort)
  return params.toString()
}

// The jobs the extension collected: the filtered list on the left of the Jobs page, and the
// job open on the right.
export const useFetchedJobsStore = defineStore('fetchedJobs', {
  state: () => ({
    jobs: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0 },
    counts: { all: 0, active: 0, applied: 0, bookmarked: 0, ignored: 0 }, // across everything the user has, ignoring the filters
    filters: defaultFilters(),
    loaded: false,
    loading: false,
    loadingMore: false,
    loadMoreError: false, // the last page failed to load; set until the next try
    refreshing: false,
    error: null,

    selectedId: null,
    pinnedId: null, // a job opened from a link: it is kept open even when it is not in the list
    details: {}, // full job by id
    detailLoading: false,
    detailError: null,

    // Filters can change faster than the server answers. Only the newest request is used.
    requestId: 0,
  }),

  getters: {
    hasMore: (state) => state.pageInfo.current_page < state.pageInfo.total_pages,
    isFiltered: (state) => {
      const { q, job_title_id, job_location_id, status, posted, view } = state.filters
      return Boolean(q.trim() || job_title_id || job_location_id || status !== 'all' || posted || view !== 'active')
    },
    selected: (state) => (state.selectedId ? state.details[state.selectedId] || null : null),
  },

  actions: {
    // What the search box suggests for what has been typed (job titles, companies, locations), from all the user's jobs in the current view,
    // not just the loaded page. Gives [] when it cannot be loaded: a missing suggestion is not worth an error message.
    async suggest(query) {
      const params = new URLSearchParams({ q: query.trim() })
      if (this.filters.view !== 'active') params.set('view', this.filters.view)
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/fetched_jobs/suggestions?${params}`, {}, false)
        return !res.js_error && res.response.success ? res.response.data.suggestions : []
      } catch {
        return []
      }
    },

    // Loads the first page for the current filters.
    async fetchJobs() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = null
      this.loadMoreError = false

      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/fetched_jobs?${buildQuery(this.filters, 1)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      if (requestId !== this.requestId) return // a newer request replaced this one

      if (!res.js_error && res.response?.success) {
        this.jobs = res.response.data.records
        this.pageInfo = res.response.data.page
        this.counts = res.response.data.counts
        this.loaded = true
      } else {
        this.error = res.response?.message || 'Failed to load your jobs.'
      }
      this.loading = false
    },

    // Reloads everything on screen without changing what the user is looking at: the same
    // filters, the same open job, no loading skeletons. Other jobs' cached details are dropped
    // because they may have changed too; the open one is replaced when the new copy arrives.
    async refresh() {
      this.refreshing = true
      try {
        const openId = this.selectedId
        await this.fetchJobs()

        const kept = {}
        if (openId) {
          const { axiosRequest } = useRequest()
          let res
          try {
            res = await axiosRequest('GET', `/api/v1/fetched_jobs/${openId}`, {}, false)
          } catch {
            res = { js_error: true }
          }
          if (!res.js_error && res.response?.success) kept[openId] = res.response.data
          else if (this.details[openId]) kept[openId] = this.details[openId]
        }
        this.details = kept
      } finally {
        this.refreshing = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore || this.loading) return

      const requestId = this.requestId
      this.loadingMore = true
      this.loadMoreError = false
      const { axiosRequest } = useRequest()
      const next = this.pageInfo.current_page + 1
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/fetched_jobs?${buildQuery(this.filters, next)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      this.loadingMore = false
      if (requestId !== this.requestId) return

      if (!res.js_error && res.response?.success) {
        const known = new Set(this.jobs.map((job) => job.id))
        this.jobs.push(...res.response.data.records.filter((job) => !known.has(job.id)))
        this.pageInfo = res.response.data.page
      } else {
        this.loadMoreError = true
      }
    },

    setFilters(patch) {
      this.filters = { ...this.filters, ...patch }
      return this.fetchJobs()
    },

    clearFilters() {
      this.filters = defaultFilters()
      return this.fetchJobs()
    },

    // Opens a job from a link (for example "Made for ..." on a resume). It stays open although it may not be in the list shown.
    openFromLink(id) {
      this.pinnedId = id
      return this.select(id)
    },

    async select(id) {
      this.selectedId = id
      this.detailError = null
      if (!id || this.details[id]) return

      this.detailLoading = true
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', `/api/v1/fetched_jobs/${id}`, {}, false)
      if (this.selectedId !== id) return // the user already picked another job

      if (!res.js_error && res.response?.success) this.details[id] = res.response.data
      else this.detailError = res.response?.message || 'Failed to load this job.'
      this.detailLoading = false
    },

    // Bookmarks or ignores a job (mark is 'bookmarked' or 'ignored'; value true sets it, false
    // clears it). The screen changes at once and is put right again if the server says no. A
    // job that no longer belongs in the current view leaves the list, and the open job moves to
    // its neighbour. Returns { ok, removed, message }.
    async setMark(id, mark, value) {
      const index = this.jobs.findIndex((job) => job.id === id)
      const entry = this.jobs[index] || this.details[id]
      if (!entry) return { ok: false, message: 'That job is no longer here.' }

      const before = { bookmarked_at: entry.bookmarked_at || null, ignored_at: entry.ignored_at || null }
      const after = marksAfter(entry, mark, value)
      const removed = index !== -1 && !inView(this.filters.view, after)

      this.counts.bookmarked += Number(Boolean(after.bookmarked_at)) - Number(Boolean(before.bookmarked_at))
      this.counts.ignored += Number(Boolean(after.ignored_at)) - Number(Boolean(before.ignored_at))
      this.counts.active = this.counts.all - this.counts.ignored
      if (this.details[id]) Object.assign(this.details[id], after)
      if (removed) {
        this.jobs.splice(index, 1)
        this.pageInfo.total_records -= 1
        if (this.selectedId === id) this.selectedId = (this.jobs[index] || this.jobs[index - 1])?.id || null
      } else if (index !== -1) {
        Object.assign(this.jobs[index], after)
      }

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/fetched_jobs/${id}`, { [mark]: value }, false)
      if (!res.js_error && res.response?.success) {
        this.details[id] = res.response.data
        return { ok: true, removed }
      }

      // Not saved: reload so the list and the totals show what the server has.
      await this.refresh()
      return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
    },

    // Records (or clears) that the user applied. Returns { ok, message }.
    async setApplied(id, applied) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/fetched_jobs/${id}`, { applied }, false)
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
      }

      const before = this.jobs.find((job) => job.id === id)
      const wasApplied = Boolean(before?.applied_at ?? this.details[id]?.applied_at)
      const job = res.response.data
      this.details[id] = job
      if (before) before.applied_at = job.applied_at
      if (applied && !wasApplied) this.counts.applied += 1
      if (!applied && wasApplied) this.counts.applied -= 1
      return { ok: true }
    },
  },
})
